import CryptoJS from 'crypto-js'
import { getFullPathname } from '@/config/app-base-path'

const ENTRY_STORAGE_KEY = 'identity-verification-anonymous-entry'
const SESSION_STORAGE_KEY = 'identity-verification-active-session'
const DEVICE_STORAGE_KEY = 'identity-verification-device-id'
const LOG_UID_STORAGE_KEY = 'identity-verification-log-uid'
const VERIFICATION_CODE_PATTERN = /^[0-9A-Za-z]{16}$/
const UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const LOG_UID_PATTERN = /^[0-9a-f]{64}$/
const LOG_UID_NAMESPACE = 'identity-ui-log:v1:'

const readJson = (storage, key) => {
  if (!storage) return null
  try {
    return JSON.parse(storage.getItem(key) || 'null')
  } catch (error) {
    removeStorageItem(storage, key)
    return null
  }
}

const writeJson = (storage, key, value) => {
  if (!storage) return false
  try {
    storage.setItem(key, JSON.stringify(value))
    return true
  } catch (error) {
    return false
  }
}

const removeStorageItem = (storage, key) => {
  if (!storage) return false
  try {
    storage.removeItem(key)
    return true
  } catch (error) {
    return false
  }
}

const createUuidV4 = cryptoProvider => {
  if (!cryptoProvider) throw new Error('当前环境不支持安全随机数')
  let uuid = typeof cryptoProvider.randomUUID === 'function'
    ? cryptoProvider.randomUUID()
    : ''
  if (UUID_V4_PATTERN.test(uuid)) return uuid
  if (typeof cryptoProvider.getRandomValues !== 'function') {
    throw new Error('当前环境不支持安全随机数')
  }
  const bytes = cryptoProvider.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0'))
  uuid = `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`
  return uuid
}

const findPathVerificationCode = (pathname = '') => {
  const normalizedPath = String(pathname || '')
  const identityRouteMatch = normalizedPath.match(/^(.*\/identity-verification)\/([^/]+)\/?$/)
  if (identityRouteMatch) {
    let verificationCode = identityRouteMatch[2]
    try {
      verificationCode = decodeURIComponent(verificationCode)
    } catch (error) {
      verificationCode = ''
    }
    return {
      verificationCode,
      sanitizedPath: identityRouteMatch[1] || getFullPathname('/identity-verification')
    }
  }

  // 专用短域名根路径直接承载 16 位短码，进入应用后统一切到无敏感参数的固定路由。
  const shortRouteMatch = normalizedPath.match(/^\/([0-9A-Za-z]{16})\/?$/)
  if (shortRouteMatch) {
    return {
      verificationCode: shortRouteMatch[1],
      sanitizedPath: getFullPathname('/identity-verification')
    }
  }
  return null
}

/**
 * 在日志、埋点和业务请求初始化前转存并移除地址中的短码。
 * GET 仅完成浏览器侧上下文建立，不会调用 open，因此短信扫描器不会消费链接。
 */
export const captureIdentityVerificationEntry = ({ location, history, sessionStorage } = {}) => {
  if (!location || !history) return null
  const pathEntry = findPathVerificationCode(location.pathname)
  const anonymousEntryPath = getFullPathname('/identity-verification')
  const searchParams = new URLSearchParams(location.search || '')
  if (searchParams.get('purpose') === 'customerService') {
    // 旧 Agent 核身链接必须直接失效，不能被误当成新的登录业务核身入口。
    removeStorageItem(sessionStorage, ENTRY_STORAGE_KEY)
    removeStorageItem(sessionStorage, SESSION_STORAGE_KEY)
    try {
      history.replaceState(history.state, '', anonymousEntryPath)
    } catch (error) {
      return null
    }
    return null
  }
  const hasQueryCode = searchParams.has('verificationCode')
  const queryCode = searchParams.get('verificationCode') || ''
  const verificationCode = pathEntry ? pathEntry.verificationCode : queryCode
  if (!pathEntry && !hasQueryCode) return null

  // query 兼容入口也切到专用匿名路由，避免与 keep-alive 的登录 `/auth/video` 共享页面实例。
  const sanitizedPath = pathEntry
    ? pathEntry.sanitizedPath
    : (hasQueryCode ? anonymousEntryPath : location.pathname)
  searchParams.delete('verificationCode')
  const sanitizedSearch = searchParams.toString()
  const sanitizedUrl = `${sanitizedPath}${sanitizedSearch ? `?${sanitizedSearch}` : ''}${location.hash || ''}`
  const validCode = VERIFICATION_CODE_PATTERN.test(verificationCode)
  let entry = null
  if (validCode) {
    entry = {
      verificationCode,
      entryPath: sanitizedPath,
      capturedAt: Date.now()
    }
    // 同一标签页打开另一条短信链接代表新任务，旧匿名会话不能覆盖新短码入口。
    removeStorageItem(sessionStorage, SESSION_STORAGE_KEY)
    writeJson(sessionStorage, ENTRY_STORAGE_KEY, entry)
  } else if (sessionStorage) {
    // 显式传入无效短码时清掉同路径旧上下文，避免错误链接误用上一次会话。
    removeStorageItem(sessionStorage, ENTRY_STORAGE_KEY)
    removeStorageItem(sessionStorage, SESSION_STORAGE_KEY)
  }
  try {
    history.replaceState(history.state, '', sanitizedUrl)
  } catch (error) {
    return null
  }
  return entry
}

/** 只允许当前净化后的入口路径读取短码，避免旧标签页中的短码串到普通登录核身。 */
export const readIdentityVerificationEntry = (storage, pathname = '') => {
  const entry = readJson(storage, ENTRY_STORAGE_KEY)
  if (!entry || entry.entryPath !== pathname || !VERIFICATION_CODE_PATTERN.test(entry.verificationCode || '')) {
    return null
  }
  return entry
}

/** open 成功后立即删除短码明文，只保留服务端会话继续上传。 */
export const clearIdentityVerificationCode = (storage) => {
  const entry = readJson(storage, ENTRY_STORAGE_KEY)
  if (!entry) return
  delete entry.verificationCode
  writeJson(storage, ENTRY_STORAGE_KEY, entry)
}

/** 将一次性短码转换为不可逆日志 UID，避免在 ui_log 中保存可使用的核身凭证。 */
export const resolveIdentityVerificationLogUid = (storage, verificationCode = '') => {
  if (VERIFICATION_CODE_PATTERN.test(String(verificationCode || ''))) {
    const logUid = CryptoJS.SHA256(`${LOG_UID_NAMESPACE}${verificationCode}`).toString()
    try {
      if (storage) storage.setItem(LOG_UID_STORAGE_KEY, logUid)
    } catch (error) {
      // 隐私模式禁用存储时仍返回本次摘要，不影响当前页面日志上报。
    }
    return logUid
  }
  try {
    const storedLogUid = storage ? storage.getItem(LOG_UID_STORAGE_KEY) || '' : ''
    return LOG_UID_PATTERN.test(storedLogUid) ? storedLogUid : ''
  } catch (error) {
    return ''
  }
}

/** 本地随机标识只用于匿名 open 限流，不从硬件、手机号或实名信息派生。 */
export const ensureIdentityVerificationDeviceId = (storage, cryptoProvider) => {
  let existing = ''
  try {
    existing = storage ? storage.getItem(DEVICE_STORAGE_KEY) || '' : ''
  } catch (error) {
    existing = ''
  }
  if (UUID_V4_PATTERN.test(existing)) return existing
  const provider = cryptoProvider || (typeof window !== 'undefined' ? window.crypto : null)
  const deviceId = createUuidV4(provider)
  try {
    if (storage) storage.setItem(DEVICE_STORAGE_KEY, deviceId)
  } catch (error) {
    // 隐私模式禁用持久化时仍可使用本次随机值，服务端还保留 code/IP 维度。
  }
  return deviceId
}

export const saveIdentityVerificationSession = (storage, session) => {
  return writeJson(storage, SESSION_STORAGE_KEY, session)
}

export const readIdentityVerificationSession = (storage, mode) => {
  const session = readJson(storage, SESSION_STORAGE_KEY)
  if (!session || session.mode !== mode || !session.entryPath) return null
  if (session.expiresAt && new Date(session.expiresAt).getTime() <= Date.now()) {
    return { ...session, status: 'EXPIRED', uiState: 'EXPIRED' }
  }
  return session
}

export const clearIdentityVerificationSession = (storage) => {
  removeStorageItem(storage, SESSION_STORAGE_KEY)
}

export const isIdentityVerificationCode = value => VERIFICATION_CODE_PATTERN.test(String(value || ''))
